import React from 'react';
import { type InteractionPart, InteractionPartUtils } from '@lib/models/InteractionPart';
import { EventType } from '@lib/models/enums';
import CodeIcon from '@mui/icons-material/Code';
import { prettiffyPythonFunctionName } from '@lib/utils/interactionIdChecker';
import MessageNonTextDisplay from '../MessageNonTextDisplay';
import FriendlyPartDisplay from '../FriendlyPartDisplay';
import type { FriendlyState } from '../friendlyToolConfig';
import type { PartRendererProps } from './types';

// `currentColor` → inherits the box header's color (theme `text.secondary`), so the
// icon is visible on both light and dark themes instead of a fixed white.
const boxedIconStyle = { fontSize: 10, color: 'currentColor' };

interface FunctionDisplay {
    title: string;
    content: string;
    elapsed?: number;
}

function deriveFunctionDisplay(part: InteractionPart): FunctionDisplay {
    if (part.eventType === EventType.PartialFunctionCall) {
        return {
            title: prettiffyPythonFunctionName(part.functionDetails?.name ?? 'Unknown'),
            content: InteractionPartUtils.getPartTextContent(part),
        };
    }

    const name = part.functionDetails?.name;

    if (!name) {
        const rawData = part.eventType === EventType.FunctionCall
            ? part.functionDetails?.args ?? {}
            : part.functionDetails?.response ?? {};
        const collapsed = InteractionPartUtils.isCharArray(rawData)
            ? rawData.join("")
            : InteractionPartUtils.tryCollapseCharIndexedObject(rawData);
        const data = collapsed ?? rawData;
        return {
            title: 'Unknown',
            content: InteractionPartUtils.jsonToString(data),
        };
    }

    let title = prettiffyPythonFunctionName(name);
    const argChartType = part.functionDetails?.args?.query_type;
    if (typeof argChartType === 'string' && argChartType.length > 1) {
        title += `: ${argChartType[0].toLocaleUpperCase()}${argChartType.slice(1)}`;
    }

    let elapsed = 0;
    if (part.functionDetails?.start && part.functionDetails?.end) {
        elapsed = part.functionDetails.end.getTime() - part.functionDetails.start.getTime();
    }

    let content: string;
    if (part.eventType === EventType.FunctionCall) {
        try {
            content = JSON.stringify(part.functionDetails?.args ?? {}, null, 4);
        } catch {
            content = String(part.functionDetails?.args ?? "{}");
        }
    } else {
        const raw = part.functionDetails?.response;
        let responseForDisplay: unknown;
        if (raw == null) {
            responseForDisplay = null;
        } else if (typeof raw === 'string') {
            responseForDisplay = raw;
        } else if (typeof raw !== 'object') {
            responseForDisplay = String(raw);
        } else {
            const collapsed = InteractionPartUtils.isCharArray(raw)
                ? raw.join("")
                : InteractionPartUtils.tryCollapseCharIndexedObject(raw);
            responseForDisplay = collapsed ?? raw;
        }
        try {
            content = JSON.stringify(
                { arguments: part.functionDetails?.args, response: responseForDisplay },
                null, 4,
            );
        } catch {
            content = String(raw ?? "");
        }
        const MAX_DATA_LENGTH = 100_000;
        if (content.length > MAX_DATA_LENGTH) {
            content = content.substring(0, MAX_DATA_LENGTH) + "\n\n... [truncated — response too large to display]";
        }
    }

    return { title, content, elapsed };
}

const FRIENDLY_STATE_MAP: Record<string, FriendlyState> = {
    [EventType.PartialFunctionCall]: 'partial',
    [EventType.FunctionCall]: 'call',
    [EventType.FunctionResponse]: 'response',
};

const FunctionPart: React.FC<PartRendererProps> = ({
    part,
    message,
    preview,
    isLastInlineInGroup,
    friendlyMode = false,
    onLinkClicked,
}) => {
    const display = deriveFunctionDisplay(part);

    if (friendlyMode) {
        const friendlyState = FRIENDLY_STATE_MAP[part.eventType] ?? 'partial';
        return (
            <FriendlyPartDisplay
                toolName={part.functionDetails?.name ?? 'unknown'}
                state={friendlyState}
                args={part.functionDetails?.args ?? InteractionPartUtils.getPartTextContent(part)}
                isError={false}
                startTime={part.functionDetails?.start}
                endTime={part.functionDetails?.end}
                streamedBytes={display.content.length}
            />
        );
    }

    // Partial function calls preserve the legacy `sql` fence — the content
    // is the streaming raw text and the fence label was set this way
    // historically. Function call/response always render as JSON.
    const markdownLang = part.eventType === EventType.PartialFunctionCall ? 'sql' : 'json';
    const markdownContent = `\`\`\`${markdownLang}\n\n${display.content}\n\n\`\`\``;
    const onDrawCodeBlock = (_c: string, _cl: string) => null;

    return (
        <MessageNonTextDisplay
            sessionId={message.sessionId ?? 'ND'}
            title={display.title}
            icon={<CodeIcon style={boxedIconStyle} />}
            elapsed={display.elapsed}
            content={markdownContent}
            part={part}
            preview={preview}
            onDrawCodeBlock={onDrawCodeBlock}
            onLinkClicked={onLinkClicked}
            isLastInlineInGroup={isLastInlineInGroup}
        />
    );
};

export default FunctionPart;
